import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import MoneyInput from "@/components/MoneyInput";
import MaterialListDialog, { materialListText } from "@/components/MaterialListDialog";
import { CATEGORY_META, PaymentMethodField } from "@/pages/Financeiro";
import { Wallet, Plus, Trash2, Loader2, CreditCard, FileBarChart, Pencil, Paperclip, Eye, X, Package, ListChecks } from "lucide-react";

export const BUDGET_EXPENSE_STATUSES = ["aprovado", "iniciando", "finalizado", "nota_fiscal_gerada", "nota_faturada"];
const POST_NF = ["nota_fiscal_gerada", "nota_faturada"];
const CATS = Object.entries(CATEGORY_META).filter(([k]) => k !== "impostos");
const TODAY = new Date().toISOString().slice(0, 10);
const blank = () => ({ date: TODAY, category: "material_escola", amount: "", description: "", payment_method: "caixa", installments: 1, informative: false, illustrative: false, receipt_path: "", material_items: [] });
const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

const ExpenseRow = ({ e, onDelete, onEdit }) => {
  const meta = CATEGORY_META[e.category] || CATEGORY_META.outros;
  return (
    <li className="py-2 flex items-start gap-2 text-xs" data-testid={`budget-exp-${e.id}`}>
      <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}><meta.icon className="w-3.5 h-3.5" /></span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 whitespace-pre-wrap break-words">{e.description || meta.label}</p>
        <p className="text-[10.5px] text-slate-500">
          {formatDateBR(e.date)} • {meta.label}
          {e.illustrative ? <> • <span className="font-bold text-teal-700">só ilustração</span></>
            : e.payment_method === "cartao" ? <> • <CreditCard className="w-3 h-3 inline" /> cartão{e.installments > 1 ? ` ${e.installments}x` : ""}</> : e.payment_method === "dia_a_dia" ? " • pago pelo Dia a Dia" : e.caixa_saida_id ? " • pago pelo Caixa" : ""}
          {e.informative && !e.illustrative && " • informativa"}
        </p>
        {e.receipt_path && (
          <a href={fileUrl(e.receipt_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-0.5 text-[10.5px] font-semibold text-sky-700 hover:underline" data-testid={`budget-exp-receipt-${e.id}`}>
            <Paperclip className="w-3 h-3" /> Ver comprovante
          </a>
        )}
      </div>
      <span className={`font-black tabular-nums ${e.illustrative ? "text-slate-400 line-through" : "text-slate-900"}`}>{BRL(e.amount)}</span>
      <button type="button" onClick={() => onEdit(e)} className="p-1.5 text-slate-400 hover:text-violet-600" data-testid={`budget-exp-edit-${e.id}`} aria-label="Editar">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => onDelete(e)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`budget-exp-del-${e.id}`} aria-label="Excluir">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </li>
  );
};

const ReceiptField = ({ form, setForm }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_path: r.data?.path || r.data?.storage_path || "" }));
      toast.success("Comprovante anexado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao anexar");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">Nota / comprovante (opcional)</label>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="budget-exp-receipt-input" />
      {form.receipt_path ? (
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 h-10 text-sm">
          <Paperclip className="w-4 h-4 text-sky-700" />
          <a href={fileUrl(form.receipt_path)} target="_blank" rel="noreferrer" className="flex-1 text-sky-700 font-semibold truncate hover:underline"><Eye className="w-3.5 h-3.5 inline mr-1" />Comprovante anexado</a>
          <button type="button" onClick={() => setForm((f) => ({ ...f, receipt_path: "" }))} className="text-slate-400 hover:text-rose-600" aria-label="Remover" data-testid="budget-exp-receipt-remove"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="w-full bg-white" data-testid="budget-exp-receipt-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />} Anexar foto ou PDF
        </Button>
      )}
    </div>
  );
};

const MaterialListButton = ({ form, setForm }) => {
  const [open, setOpen] = useState(false);
  const n = form.material_items?.length || 0;
  const apply = (items, total) => setForm((f) => ({ ...f, material_items: items, amount: total, description: materialListText(items), category: "material_escola" }));
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid="budget-exp-material-list"
        className={`w-full h-11 ${n ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-dashed border-[#7c3aed]/50 bg-white text-[#7c3aed]"}`}>
        <ListChecks className="w-4 h-4 mr-1.5" /> {n ? `Lista de materiais (${n} ${n === 1 ? "item" : "itens"}) · editar` : "Lista de materiais"}
      </Button>
      <MaterialListDialog open={open} onOpenChange={setOpen} initial={form.material_items} onSave={apply} />
    </>
  );
};

const ExpenseFields = ({ form, setForm, postNF }) => (
  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="budget-exp-form">
    <MaterialListButton form={form} setForm={setForm} />
    <label className={`flex items-start gap-2 text-[11.5px] rounded-lg p-2.5 cursor-pointer border ${form.illustrative ? "bg-teal-50 border-teal-300 text-teal-900" : "bg-white border-slate-200 text-slate-700"}`}>
      <Checkbox checked={form.illustrative} onCheckedChange={(v) => setForm({ ...form, illustrative: !!v })} data-testid="budget-exp-illustrative" className="mt-0.5" />
      <span><b className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" />Só ilustração (material próprio)</b><br />Fica salvo como material utilizado, mas não sai do Caixa nem do Cartão e não entra no cálculo.</span>
    </label>
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">Categoria</label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {CATS.map(([k, m]) => (
          <button key={k} type="button" onClick={() => setForm({ ...form, category: k })} data-testid={`budget-exp-cat-${k}`}
            className={`rounded-lg border px-2 py-2 text-[11px] font-bold transition-colors ${form.category === k ? "border-[#7c3aed] bg-[#7c3aed]/5 text-[#7c3aed]" : "border-slate-200 bg-white text-slate-600"}`}>
            {m.label}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">Data</label>
      <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full min-w-0 h-10 appearance-none bg-white" data-testid="budget-exp-date" />
    </div>
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">{form.illustrative ? "Valor de referência (opcional)" : "Valor (R$)"}</label>
      <MoneyInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className="w-full bg-white" data-testid="budget-exp-amount" />
    </div>
    {!form.illustrative && <PaymentMethodField form={form} setForm={setForm} withDiaADia caixaHint="Sai do Caixa da empresa agora (aparece nas saídas do Caixa)." />}
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">Descrição</label>
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 200)}px`; } }}
        rows={1}
        placeholder="Ex.: Tinta, cimento, diária"
        className="w-full min-h-[40px] max-h-[200px] resize-none overflow-y-auto rounded-md border border-input bg-white px-3 py-2 text-sm leading-snug focus:outline-none focus:ring-2 focus:ring-ring"
        data-testid="budget-exp-desc"
      />
    </div>
    <ReceiptField form={form} setForm={setForm} />
    {postNF && !form.illustrative && (
      <label className="flex items-start gap-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2 cursor-pointer">
        <Checkbox checked={form.informative} onCheckedChange={(v) => setForm({ ...form, informative: !!v })} data-testid="budget-exp-informative" className="mt-0.5" />
        <span><b>Apenas informativa</b> — não entra no cálculo. Deixe desmarcado para recalcular: valores já repassados viram ajuste (nada é apagado).</span>
      </label>
    )}
  </div>
);

export default function BudgetExpensesPanel({ budget, canAdd, onOpenResult }) {
  const postNF = POST_NF.includes(budget.status);
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(blank());
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/expenses", { params: { budget_id: budget.id } });
    setRows(r.data || []);
  };
  useEffect(() => { load().catch(() => setRows([])); }, [budget.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => { setForm(blank()); setOpen(false); setEditingId(null); };

  const startEdit = (e) => {
    setForm({
      date: e.date, category: e.category, amount: Number(e.amount) || 0, description: e.description || "",
      payment_method: e.illustrative ? "caixa" : (e.payment_method || "caixa"), installments: e.installments || 1,
      informative: !!e.informative && !e.illustrative, illustrative: !!e.illustrative, receipt_path: e.receipt_path || "", material_items: e.material_items || [],
    });
    setEditingId(e.id);
    setOpen(true);
  };

  const save = async () => {
    const amount = parseFloat(String(form.amount || 0).replace(",", ".")) || 0;
    if (!form.illustrative && amount <= 0) { toast.error("Informe um valor válido"); return; }
    setSaving(true);
    const body = {
      ...form, amount, budget_id: budget.id,
      installments: !form.illustrative && form.payment_method === "cartao" ? Number(form.installments) || 1 : 1,
      informative: form.illustrative || (postNF && !!form.informative),
    };
    try {
      if (editingId) {
        await api.patch(`/expenses/${editingId}`, body);
        toast.success("Despesa atualizada");
      } else {
        await api.post("/expenses", body);
        toast.success(form.illustrative ? "Material salvo (só ilustração)" : "Despesa lançada no orçamento");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar despesa");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm("Excluir esta despesa?")) return;
    try {
      await api.delete(`/expenses/${e.id}`);
      toast.success("Despesa excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const total = (rows || []).filter((e) => !e.informative).reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-slate-100 card-shadow p-5" data-testid="budget-expenses-panel">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-4 h-4 text-rose-600" />
        <h3 className="font-heading text-sm font-semibold text-slate-900 flex-1">Despesas deste orçamento</h3>
        <span className="text-sm font-black tabular-nums text-rose-700" data-testid="budget-exp-total">{BRL(total)}</span>
      </div>
      {rows === null ? (
        <div className="py-3 text-xs text-slate-500 flex items-center"><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Carregando…</div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-slate-500 italic mb-3">Nenhuma despesa lançada neste orçamento.</p>
      ) : (
        <ul className="divide-y divide-slate-100 mb-3">{rows.map((e) => <ExpenseRow key={e.id} e={e} onDelete={remove} onEdit={startEdit} />)}</ul>
      )}
      {open && (
        <>
          {editingId && <p className="text-[11px] font-bold text-violet-700 mb-1.5" data-testid="budget-exp-editing">Editando despesa</p>}
          <ExpenseFields form={form} setForm={setForm} postNF={postNF} />
        </>
      )}
      <div className="flex gap-2 mt-3 flex-wrap">
        <Button variant="outline" onClick={onOpenResult} className="flex-1 min-w-[140px]" data-testid="budget-open-result">
          <FileBarChart className="w-4 h-4 mr-1" /> Demonstrativo
        </Button>
        {open && <Button variant="outline" onClick={close} className="flex-1" data-testid="budget-exp-cancel">Cancelar</Button>}
        {(canAdd || open) && (
        <Button onClick={open ? save : () => setOpen(true)} disabled={saving} className="flex-1 min-w-[140px] bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid={open ? "budget-exp-save" : "budget-exp-add"}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          {open ? (editingId ? "Salvar alterações" : "Salvar despesa") : "Lançar despesa"}
        </Button>
        )}
      </div>
      {!canAdd && <p className="text-[10.5px] text-slate-500 mt-2">Despesas podem ser lançadas a partir do status Aprovado.</p>}
    </div>
  );
}
